import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetExpenseReceiptAttachmentIds } from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import {
  selectDeletingExpenseFromState,
  selectErrorDetailMapFromState,
  selectExpenseIdFromRoute,
  selectPatchingExpenseFromState,
  selectReceiptAttachmentIdsFromState,
  selectTopLevelError,
  selectUploadingAttachmentsFromState,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.selectors'
import ExpenseFlyoutHeader from 'src/components/ExpenseFlyoutHeader/ExpenseFlyoutHeader'
import ExpenseForm, {
  ExpenseFields,
} from 'src/components/ExpenseForm/ExpenseForm'
import LoadingRightFlyoutContent from 'src/components/LoadingRightFlyoutContent/LoadingRightFlyoutContent'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  ExpenseModel,
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { expenseReportsQueries } from 'src/queries/expenseReports/expenseReports.queries'
import { expenseQueries } from 'src/queries/expenses/expenses.queries'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { createSetIsDeleteExpensePopupOpen } from 'src/redux/actions/app.actions'
import { patchExpenseAndOpenToastAndCloseFlyout } from 'src/redux/actions/data/expenses.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectReceiptAttachmentsListModels } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toCents, toDollars } from 'src/utils/currency.utils'

const UpdatExpenseFlyout: React.FC = () => {
  const translate = useTranslate()

  const expenseId = useSelector(selectExpenseIdFromRoute)
  const expenseQuery = useQuery(expenseQueries.detail(expenseId))
  const expense = useMemo(
    () =>
      expenseQuery.data !== undefined
        ? new ExpenseModel(expenseQuery.data)
        : undefined,
    [expenseQuery.data],
  )
  // Report-detail is chained on the loaded expense's reportId (the factory
  // enabled-guards it), preserving the legacy "fetch report only after the
  // expense resolves" timing.
  const reportQuery = useQuery(
    expenseReportsQueries.reportDetail(expense?.expenseReportId),
  )
  const expenseReport = useMemo(
    () =>
      reportQuery.data !== undefined
        ? new ExpenseReportModel(reportQuery.data)
        : undefined,
    [reportQuery.data],
  )
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const patchingExpense = useSelector(selectPatchingExpenseFromState)
  const deletingExpense = useSelector(selectDeletingExpenseFromState)
  const receiptAttachmentIds = useSelector(selectReceiptAttachmentIdsFromState)
  const uploadedReceiptModels = useSelector(selectReceiptAttachmentsListModels)
  // Receipt models: uploads live in the data.receiptAttachments store (seeded
  // by UPLOAD_ATTACHMENT_SUCCESS); the expense's original attachments used to be
  // seeded there by GET_EXPENSE_SUCCESS, which no longer fires now that the
  // expense read is React Query. Source originals from the expense query so the
  // displayed/editable receipt list stays complete.
  const receiptAttachments = useMemo(() => {
    const originalsById = (expenseQuery.data?.receiptAttachments ?? []).reduce<
      Record<string, ReceiptAttachmentModel>
    >((acc, attachment) => {
      acc[attachment.id] = new ReceiptAttachmentModel(attachment)
      return acc
    }, {})
    return receiptAttachmentIds
      .map((id) => uploadedReceiptModels[id] ?? originalsById[id])
      .filter((model): model is ReceiptAttachmentModel => model !== undefined)
  }, [receiptAttachmentIds, uploadedReceiptModels, expenseQuery.data])
  const topLevelError = useSelector(selectTopLevelError)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const loaded = !expenseQuery.isLoading
  const user = useCurrentUser()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { data: hasOutOfPocketExpenses } = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    hasOutOfPocketExpenses,
  )

  const isApproverOrSubmitter =
    user?.id === expenseReport?.employeeId ||
    user?.id === organization?.expenseApprover?.id

  const isSubmitter = user?.id === expenseReport?.employeeId
  const isApprover = user?.id === organization?.expenseApprover?.id

  const notesAndReceiptsEditable =
    (expenseReport?.status === ExpenseReportStatus.Pending ||
      expenseReport?.status === ExpenseReportStatus.Draft) &&
    isApproverOrSubmitter
  const fieldsEditable = expenseReport?.status === ExpenseReportStatus.Draft
  const hasViewOnlyPermission = user?.hasOrgBookkeeperPermissions

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const isManager = directReports !== undefined && directReports.length > 0

  const dispatch = useFlyoutDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments()
  const canViewExpenses =
    isApproverOrSubmitter || hasViewOnlyPermission || isManager

  const { analytics } = useAnalyticsContext()

  const baseFlatFields = useMemo(
    (): ExpenseFields => ({
      expenseName: expense?.expenseName,
      currencyCode: expense?.currencyCode,
      expenseReportId: expenseReport?.id,
      expenseDate:
        expense?.expenseDate !== undefined
          ? moment(expense.expenseDate)
          : undefined,
      amountCents:
        expense?.amountCents !== undefined
          ? toDollars(expense.amountCents)
          : undefined,
      notes: expense?.notes,
      type: expense?.type,
      distance: expense?.distance,
      mileageRate:
        expense?.mileageRate !== undefined
          ? expense?.mileageRate / 1000
          : undefined,
      useMileageRoute: expense?.useMileageRoute,
      mileageRoute: expense?.mileageRoute,
    }),
    [expense],
  )

  const negativeButton = {
    loading: deletingExpense,
    text: translate('Delete'),
    onClick: (): void => {
      dispatch(createSetIsDeleteExpensePopupOpen(true, [expenseId]))
    },
  }

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetExpenseReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const dispatchPatchExpense = (
    body: ExpenseFields,
    amountToBeReimbursed: number,
    isExpenseTypeMileage: boolean,
  ): void => {
    const trackUpdate = (): void => {
      const includeMethod = fieldsEditable && isExpenseTypeMileage
      analytics?.track('update reimbursement expense', {
        expense_type: body.type === 'MILEAGE' ? 'Mileage' : 'General',
        amount_spent: body.amountCents ? body.amountCents : undefined,
        distance: body.distance ?? body.mileageRoute?.distance,
        expense_name: expenseReport?.reportName,
        receipt_attached: receiptAttachments && receiptAttachments.length > 0,
        add_notes: !!body.notes,
        ...(includeMethod && {
          mileage_calculation_method: body.useMileageRoute
            ? 'google_maps'
            : 'manual',
        }),
        ...(includeMethod &&
          body.useMileageRoute && {
            stops_count: body.mileageRoute?.stops?.length ?? 0,
          }),
      })
    }

    if (!fieldsEditable) {
      dispatch(
        patchExpenseAndOpenToastAndCloseFlyout(
          expense?.id,
          {
            expenseReportId: expense?.expenseReportId,
            notes: body.notes !== undefined ? body.notes : undefined,
            receiptAttachmentIds: receiptAttachments.map((att) => att.id),
          },
          analytics,
        ),
      )
      trackUpdate()
      return
    }

    const changedFields: Record<string, unknown> = {}

    if (body.expenseName !== baseFlatFields.expenseName) {
      changedFields.expenseName = body.expenseName
    }
    if (body.expenseDate?.valueOf() !== baseFlatFields.expenseDate?.valueOf()) {
      changedFields.expenseDate = body.expenseDate
    }
    if (body.notes !== baseFlatFields.notes) {
      changedFields.notes = body.notes
    }
    if (body.type !== baseFlatFields.type) {
      changedFields.type = body.type
    }

    const currentAttachmentIds = receiptAttachments.map((att) => att.id)
    const originalAttachmentIds =
      expense?.receiptAttachments?.map((att) => att.id) ?? []
    if (
      JSON.stringify(currentAttachmentIds) !==
      JSON.stringify(originalAttachmentIds)
    ) {
      changedFields.receiptAttachmentIds = currentAttachmentIds
    }

    if (isExpenseTypeMileage) {
      const newAmountCents = toCents(amountToBeReimbursed)
      if (newAmountCents !== expense?.amountCents) {
        changedFields.amountCents = newAmountCents
      }

      const bodyUseMileageRoute = body.useMileageRoute ?? true
      changedFields.useMileageRoute = bodyUseMileageRoute

      if (bodyUseMileageRoute) {
        changedFields.distance = null
        changedFields.mileageRoute = body.mileageRoute ?? null
      } else {
        changedFields.distance = body.distance
        changedFields.mileageRoute = null
      }
    } else {
      const newAmountCents =
        body.amountCents !== undefined ? toCents(body.amountCents) : 0
      if (newAmountCents !== expense?.amountCents) {
        changedFields.amountCents = newAmountCents
      }
    }

    dispatch(
      patchExpenseAndOpenToastAndCloseFlyout(
        expense?.id,
        {
          ...changedFields,
          expenseReportId: expense?.expenseReportId,
        } as ExpenseFields,
        analytics,
      ),
    )
    trackUpdate()
  }

  useEffect((): void => {
    if (expense?.receiptAttachments !== undefined) {
      dispatch(
        createSetExpenseReceiptAttachmentIds(
          expense?.receiptAttachments.map((att) => att.id),
        ),
      )
    } else {
      dispatch(createSetExpenseReceiptAttachmentIds([]))
    }
  }, [expense?.receiptAttachments])

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {outOfPocketExpensesEnabled &&
      expenseReport !== undefined &&
      expense !== undefined &&
      canViewExpenses ? (
        <>
          <span>
            <ExpenseFlyoutHeader
              reportName={expenseReport.reportName}
              flyoutTitle={
                fieldsEditable || notesAndReceiptsEditable
                  ? 'Update Expense'
                  : 'Reimbursement'
              }
            />
            <ExpenseForm
              expenseReportId={expense.expenseReportId}
              expenseReportStatus={expenseReport.status}
              expense={expense}
              isSubmitter={isSubmitter}
              isApprover={isApprover}
              isManager={isManager}
              negativeButton={negativeButton}
              loading={patchingExpense}
              deleteAttachment={deleteAttachment}
              errorDetailMap={errorDetailMap}
              receiptAttachments={receiptAttachments}
              uploadAttachments={dispatchUploadAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              topLevelError={topLevelError}
              buttonText={translate('Update Expense')}
              baseFlatFields={baseFlatFields}
              notesAndReceiptsEditable={notesAndReceiptsEditable}
              fieldsEditable={fieldsEditable}
              mileageRate={organization?.mileageRateThousandths}
              onSuccessfulSubmit={dispatchPatchExpense}
            />
          </span>
        </>
      ) : loaded ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <LoadingRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default UpdatExpenseFlyout
